import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Anchor, Badge, Button, Container, Group, Table, Text, Title } from "@mantine/core"
import { IconAlertTriangle, IconClipboardList, IconSearch } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { EmptyState } from "../../../../components/EmptyState"
import { PageHeaderSkeleton, TableSkeleton } from "../../../../components/PageSkeletons"
import { SearchInput } from "../../../../components/SearchInput"
import { formatDateTime, parsePgTimestamp } from "../../../../lib/format-date"
import type { IViewProps } from "./interfaces"

// Map each action to a SEMANTIC palette by what it MEANS (per theming rules) —
// not a literal hue. Reads stay "info", edits "warning", processing/config
// "brand", removals "danger", restorations/creations "success".
const actionColors: Record<string, string> = {
  // Reads / neutral events
  view_meeting: "info",
  view_transcript: "info",
  view_audit_log: "info",
  login: "info",
  logout: "info",
  export_meeting: "info",
  resolve_speaker: "info",
  // Creations / additions / restorations
  create_meeting: "success",
  create_company: "success",
  create_series: "success",
  create_character: "success",
  create_speaker: "success",
  add_domain: "success",
  verify_domain: "success",
  add_attendee: "success",
  grant_access: "success",
  unhide_meeting: "success",
  // Edits / config / processing
  update_meeting_subject: "warning",
  edit_transcript: "warning",
  edit_analysis: "warning",
  rename_speaker: "warning",
  update_series: "warning",
  update_character: "warning",
  update_speaker: "warning",
  update_settings: "brand",
  configure_sso: "brand",
  set_primary_domain: "brand",
  assign_template: "brand",
  reprocess: "brand",
  translate_analysis: "brand",
  merge_meetings: "brand",
  stop_recording: "brand",
  stop_streaming: "brand",
  upload_audio: "info",
  // Removals / destructive
  hide_meeting: "danger",
  remove_domain: "danger",
  remove_attendee: "danger",
  revoke_access: "danger",
  delete_meeting: "danger",
  delete_series: "danger",
  delete_character: "danger",
  delete_speaker: "danger",
  delete_voiceprint: "danger",
}

/**
 * Audit log page — pure presentation. The smart route wrapper owns the query +
 * search filtering; this view renders the loading/error/empty states, the
 * controlled search input, and the entry table.
 */
export const AuditView = ({
  entries,
  isLoading,
  error,
  search,
  onSearchChange,
  isFiltered,
  onRetry,
  emptyMessage,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()

  if (isLoading) {
    return (
      <Container size="lg">
        <PageHeaderSkeleton />
        <TableSkeleton rows={8} cols={6} />
      </Container>
    )
  }
  if (error) {
    return (
      <Container size="lg">
        <EmptyState
          tone="danger"
          icon={<IconAlertTriangle />}
          title={<Trans>Couldn't load the audit log</Trans>}
          description={error}
          action={
            onRetry ? (
              <Button variant="default" onClick={onRetry}>
                <Trans>Try again</Trans>
              </Button>
            ) : undefined
          }
        />
      </Container>
    )
  }

  return (
    <Container size="lg">
      <Group justify="space-between" mb="md">
        <Title order={2}>
          <Trans>Audit log</Trans>
        </Title>
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder={i18n._(msg`Filter by user, action, meeting…`)}
        />
      </Group>

      <Text size="sm" c="dimmed" mb="md">
        <Trans>
          {entries.length} entries {isFiltered ? "(filtered)" : ""}
        </Trans>
      </Text>

      {entries.length === 0 ? (
        <EmptyState
          tone="neutral"
          icon={isFiltered ? <IconSearch /> : <IconClipboardList />}
          title={isFiltered ? <Trans>No entries match your search</Trans> : <Trans>No audit entries yet</Trans>}
          description={
            isFiltered ? (
              <Trans>Try a different user, action, or meeting.</Trans>
            ) : (
              (emptyMessage ?? <Trans>Actions across your workspace will appear here as they happen.</Trans>)
            )
          }
        />
      ) : (
        // 6 columns overflow a 402px phone; let the table scroll horizontally
        // inside its container instead of bleeding past the viewport edge.
        <Table.ScrollContainer minWidth={680}>
          <Table striped highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Trans>Time</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>User</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Action</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Meeting</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Details</Trans>
                </Table.Th>
                <Table.Th visibleFrom="sm">
                  <Trans>IP</Trans>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(i18n, parsePgTimestamp(e.timestamp))}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" title={e.user_email}>
                      {e.user_name || e.user_email.split("@")[0]}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={actionColors[e.action] || "gray"} variant="light" size="sm">
                      {e.action.replace(/_/g, " ")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {e.meeting_id ? (
                      <Anchor
                        renderRoot={(props) => <Link to="/meetings/$id" params={{ id: e.meeting_id }} {...props} />}
                        size="sm"
                      >
                        {e.meeting_subject || `${e.meeting_id.substring(0, 20)}...`}
                      </Anchor>
                    ) : (
                      "—"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={2} title={e.details || undefined}>
                      {e.details || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td visibleFrom="sm">
                    <Text size="xs" c="dimmed" ff="monospace">
                      {e.ip_address || "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Container>
  )
}
